
import {
  confirmUpload,
  createBatch,
  createSidecarBatch,
  type AssetUploadMetadata,
  type CreateBatchRequest,
  type CreateBatchResponse,
} from '../../api';
import { toUploadRequests, type AcceptedFile } from './constants';
import { uploadScript } from './filmScript';
import { putFileToSignedUrl, type PutFile } from './putFileToSignedUrl';
import { toSidecarRequests, type SidecarFile } from './pairSidecars';
import { describeError } from '../../describeError';

/**
 * The upload orchestration, as a plain async function.
 *
 * Kept out of the React hook so it can be tested by calling it: the interesting
 * behaviour here is the two-step-per-file sequence, the progress arithmetic and
 * what happens when one file fails, none of which needs a rendered component to
 * exercise. `useBatchUpload` is the thin wrapper that turns `onChange` into
 * state.
 *
 * The sequence is one `POST /batches` for the whole set, then per file a PUT
 * straight to GCS followed by a confirm. The batch call is deliberately not
 * per-file: it is where the backend does its one batched signing round trip,
 * and splitting it would reintroduce the per-file IAM call that
 * `uploads.py` explicitly moved away from.
 */

/** Two files at a time was tuned for a proxied upload; these go direct to GCS. */
const UPLOAD_CONCURRENCY = 3;

export type UploadItemStatus = 'pending' | 'uploading' | 'done' | 'failed';

export interface UploadItem {
  filename: string;
  status: UploadItemStatus;
  /** 0–100. Reaches `PUT_PROGRESS_CEILING` on the PUT, 100 only after confirm. */
  progress: number;
  error?: string;
  /** Ids of assets with identical bytes. Advisory — never blocks the upload. */
  duplicateOf?: string[];
  /**
   * Why the film's script did not attach. The film itself is uploaded, so the
   * row is still `done`: the script can be added from the film while staged.
   */
  scriptError?: string;
}

/** A file to upload, and for a film, the script to attach once it is staged. */
export interface UploadFile extends AcceptedFile {
  script?: File;
}

export interface BatchUploadResult {
  batchId: string | null;
  uploaded: number;
  failed: number;
  /** True when the user cancelled, so callers can skip the "N failed" noise. */
  cancelled: boolean;
}

/**
 * Injected so tests can drive the sequence without a network or a real GCS.
 * Defaults are the real implementations.
 */
export interface BatchUploadDeps {
  createBatch: typeof createBatch;
  createSidecarBatch: typeof createSidecarBatch;
  confirmUpload: typeof confirmUpload;
  uploadScript: typeof uploadScript;
  putFile: PutFile;
}

// Re-exported for the runners that already take it from here.
export { putFileToSignedUrl };

const DEFAULT_DEPS: BatchUploadDeps = {
  createBatch,
  createSidecarBatch,
  confirmUpload,
  uploadScript,
  putFile: putFileToSignedUrl,
};

export function isAbort(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      (error as { code?: string }).code === 'ERR_CANCELED')
  );
}

// Lives in `describeError.ts` so code outside the upload flow can show the same
// messages without loading these runners; re-exported for the callers here.
export { describeError };

export interface RunBatchUploadOptions {
  accepted: UploadFile[];
  /** Which kind every file becomes. Checked against each file's media kind server-side. */
  assetType: CreateBatchRequest['asset_type'];
  label?: string | null;
  /** Batch-level metadata, stamped onto every asset the batch creates. */
  metadata?: AssetUploadMetadata | null;
  signal?: AbortSignal;
  /** Called with the full list on every transition, so React can setState from it. */
  onChange: (items: UploadItem[]) => void;
  deps?: Partial<BatchUploadDeps>;
}

export function runBatchUpload({
  accepted,
  assetType,
  label,
  metadata,
  deps: overrides,
  ...rest
}: RunBatchUploadOptions): Promise<BatchUploadResult> {
  const deps = { ...DEFAULT_DEPS, ...overrides };
  return runUploadPlan({
    ...rest,
    accepted,
    deps,
    openBatch: (signal) =>
      deps.createBatch(
        {
          asset_type: assetType,
          files: toUploadRequests(accepted),
          label: label?.trim() ? label.trim() : null,
          metadata: metadata ?? null,
        },
        signal,
      ),
  });
}

export interface RunSidecarUploadOptions {
  /** Only the images the check accepted: the server opens a row for nothing else. */
  accepted: SidecarFile[];
  label?: string | null;
  signal?: AbortSignal;
  onChange: (items: UploadItem[]) => void;
  deps?: Partial<BatchUploadDeps>;
}

/**
 * Upload with JSON: the same PUT-and-confirm run, opened by
 * `POST /sidecar-uploads`, which reads each image's metadata from the JSON
 * paired with it instead of taking one set for the batch. Only the images are
 * uploaded; their JSON travels in the request.
 */
export function runSidecarUpload({
  accepted,
  label,
  deps: overrides,
  ...rest
}: RunSidecarUploadOptions): Promise<BatchUploadResult> {
  const deps = { ...DEFAULT_DEPS, ...overrides };
  return runUploadPlan({
    ...rest,
    accepted,
    deps,
    openBatch: (signal) =>
      deps.createSidecarBatch(
        { files: toSidecarRequests(accepted), label: label?.trim() ? label.trim() : null },
        signal,
      ),
  });
}

interface RunUploadPlanOptions {
  accepted: UploadFile[];
  openBatch: (signal?: AbortSignal) => Promise<CreateBatchResponse>;
  signal?: AbortSignal;
  onChange: (items: UploadItem[]) => void;
  deps: BatchUploadDeps;
}

async function runUploadPlan({
  accepted,
  openBatch,
  signal,
  onChange,
  deps,
}: RunUploadPlanOptions): Promise<BatchUploadResult> {
  const items: UploadItem[] = accepted.map(({ file }) => ({
    filename: file.name,
    status: 'pending',
    progress: 0,
  }));

  // One array, mutated in place and published as a copy. The alternative —
  // threading state updates back through a reducer — buys nothing here because
  // only this function ever writes, and the workers need to read each other's
  // progress to know when they are done.
  const publish = () => onChange(items.map((item) => ({ ...item })));

  if (accepted.length === 0) {
    return { batchId: null, uploaded: 0, failed: 0, cancelled: false };
  }

  publish();

  let batch: CreateBatchResponse;
  try {
    batch = await openBatch(signal);
  } catch (error) {
    // A cancel during signing is not a failure to report: nothing was created
    // that the user needs to know about, and the modal is on its way out.
    if (isAbort(error)) {
      for (const item of items) {
        item.status = 'failed';
        item.error = 'Cancelled';
      }
      publish();
      return { batchId: null, uploaded: 0, failed: items.length, cancelled: true };
    }
    // The batch failed, so no file was ever going to upload. Marking them all
    // failed with the same reason beats a modal of untouched rows next to one
    // toast, which reads as "nothing happened yet".
    const reason = describeError(error, 'Could not start the upload');
    for (const item of items) {
      item.status = 'failed';
      item.error = reason;
    }
    publish();
    return { batchId: null, uploaded: 0, failed: items.length, cancelled: false };
  }

  // The backend builds `uploads` by zipping over the request's file list, so
  // index i is the target for accepted[i]. Verified rather than assumed, and
  // by filename rather than by length: if the server ever reorders or filters,
  // a length check still passes while every file goes to another file's signed
  // URL — landing under the wrong `asset_id` with the wrong `filename_original`
  // and raising nothing.
  const mispaired =
    batch.uploads.length !== accepted.length ||
    accepted.some(({ file }, index) => batch.uploads[index].filename !== file.name);

  if (mispaired) {
    for (const item of items) {
      item.status = 'failed';
      item.error = 'Server returned a mismatched upload plan';
    }
    publish();
    return { batchId: batch.batch_id, uploaded: 0, failed: items.length, cancelled: false };
  }

  let cursor = 0;
  let cancelled = false;

  const processNext = async (): Promise<void> => {
    const index = cursor++;
    if (index >= accepted.length) return;

    const { file, script } = accepted[index];
    const target = batch.uploads[index];

    items[index].status = 'uploading';
    publish();

    try {
      // `target.content_type`, not the locally resolved one: the signature is
      // computed over the value the server signed with, so that is the only
      // value GCS will accept.
      await deps.putFile(target.upload_url, file, target.content_type, {
        signal,
        onProgress: (percent) => {
          items[index].progress = percent;
          publish();
        },
      });

      // Bytes are in GCS but the asset is still `uploading` until this returns.
      // A failure here is not a lost file so much as an unclaimed one: the row
      // stays `uploading` and the Staged tab filters it out.
      const confirmed = await deps.confirmUpload(target.asset_id, signal);
      if (confirmed.duplicate_of.length > 0) {
        items[index].duplicateOf = confirmed.duplicate_of;
      }

      // Only once the film is staged: a script belongs to an asset, and until
      // confirm returns there is none. Its failure is the script's alone — the
      // film is uploaded, and sending it again would only make a duplicate.
      if (script) {
        try {
          await deps.uploadScript(target.asset_id, script, { signal });
        } catch (error) {
          if (isAbort(error)) {
            // The film stays uploaded; the cancel still stops everything else.
            items[index].status = 'done';
            items[index].progress = 100;
            items[index].scriptError = 'Cancelled';
            throw error;
          }
          items[index].scriptError = describeError(error, 'Upload failed');
        }
      }

      items[index].status = 'done';
      items[index].progress = 100;
      publish();
    } catch (error) {
      if (isAbort(error)) {
        cancelled = true;
        // Only work that had not finished either way. A file that already
        // failed keeps its real reason — overwriting "Not a valid image:
        // logo.svg" with "Cancelled" destroys the one diagnosis the user
        // needs to fix it before retrying.
        for (const item of items) {
          if (item.status === 'pending' || item.status === 'uploading') {
            item.status = 'failed';
            item.error = 'Cancelled';
          }
        }
        cursor = accepted.length;
        publish();
        return;
      }

      items[index].status = 'failed';
      items[index].error = describeError(error, 'Upload failed');
      publish();
    }
  };

  const worker = async () => {
    while (cursor < accepted.length) {
      await processNext();
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(UPLOAD_CONCURRENCY, accepted.length) }, () => worker()),
  );

  const uploaded = items.filter((item) => item.status === 'done').length;

  return {
    batchId: batch.batch_id,
    uploaded,
    failed: items.length - uploaded,
    cancelled,
  };
}
